import React, { useMemo, useState } from 'react';
import {
  ArrowLeft, FileText, Sparkles, ChevronRight, Pencil, AlertOctagon,
  History as HistoryIcon, Mic, Camera, ShieldCheck, Save, X,
} from 'lucide-react';
import {
  DOMAINS, REGISTRO_ESTADOS, ESTADOS_EDITABLES, VALUE_LABELS, CONTEXTO, CONDUCTS,
} from '../../data/observationEngine';

const fmtFecha = (iso) => new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtHora = (iso) => new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });

// Resuelve label de conducta desde catálogo local (Sprint 2) por code (ej: 'D2-C03')
const conductaLabel = (conducta) => {
  if (!conducta) return '—';
  if (conducta.label && conducta.label !== conducta.code) return conducta.label;
  const dom = (conducta.code || '').split('-')[0];
  const found = (CONDUCTS[dom] || []).find((c) => c.code === conducta.code);
  return found?.label || conducta.code || '—';
};

/**
 * Mis Registros — lista, detalle y edición de las observaciones del usuario.
 *
 * - Sólo muestra las observaciones del usuario autenticado (filtrado por session.nombre).
 * - Estados visibles: Registrado, Editado, Marcado como erróneo, En revisión,
 *   En seguimiento, Cerrado (los 3 últimos sólo los cambia el Encargado/a desde
 *   dashboard web — no editable desde móvil).
 * - Edición permitida sólo cuando estado ∈ ESTADOS_EDITABLES y el registro es propio.
 *   Cada edición empuja una versión al array versions[] (nunca sobreescribe).
 * - No hay borrar. "Marcar como registro erróneo" cambia estado a 'marcado_erroneo'.
 */
const ScreenMisRegistros = ({ onBack, observaciones, session, onUpdateObservacion, loading = false }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftEdit, setDraftEdit] = useState(null);

  // Las observaciones ya vienen filtradas por RLS (sólo del usuario actual y del modo activo)
  const propias = useMemo(() => observaciones || [], [observaciones]);

  const selected = useMemo(
    () => propias.find((o) => o.id === selectedId) || null,
    [propias, selectedId]
  );

  const startEdit = () => {
    if (!selected) return;
    setDraftEdit({
      estudiante: selected.estudiante || '',
      contexto: selected.contexto || '',
      nota: selected.evidencia?.nota || '',
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraftEdit(null); };

  const saveEdit = () => {
    if (!selected || !draftEdit) return;
    // Snapshot del registro actual ANTES de aplicar cambios (auditoría)
    const versionSnapshot = {
      versionedAt: new Date().toISOString(),
      versionedBy: session?.nombre || 'desconocido',
      estado_previo: selected.estado || 'registrado',
      curso: selected.curso,
      estudiante: selected.estudiante,
      contexto: selected.contexto,
      nota: selected.evidencia?.nota || '',
    };
    const updated = {
      ...selected,
      estado: 'editado',
      estudiante: draftEdit.estudiante,
      contexto: draftEdit.contexto,
      evidencia: { ...selected.evidencia, nota: draftEdit.nota },
      versions: [...(selected.versions || []), versionSnapshot],
      updatedAt: new Date().toISOString(),
    };
    onUpdateObservacion(updated);
    setEditing(false);
    setDraftEdit(null);
  };

  const marcarErroneo = () => {
    if (!selected) return;
    if (!window.confirm('¿Marcar este registro como erróneo? Quedará archivado pero no se eliminará.')) return;
    const versionSnapshot = {
      versionedAt: new Date().toISOString(),
      versionedBy: session?.nombre || 'desconocido',
      action: 'marcado_erroneo',
      estado_previo: selected.estado || 'registrado',
    };
    onUpdateObservacion({
      ...selected,
      estado: 'marcado_erroneo',
      versions: [...(selected.versions || []), versionSnapshot],
      updatedAt: new Date().toISOString(),
    });
  };

  // === DETALLE / EDICIÓN ===
  if (selected) {
    const dom = DOMAINS.find((d) => d.id === selected.dominio?.id) || selected.dominio;
    const estadoKey = selected.estado || 'registrado';
    const estado = REGISTRO_ESTADOS[estadoKey] || REGISTRO_ESTADOS.registrado;
    const editable = ESTADOS_EDITABLES.includes(estadoKey);

    return (
      <div className="step-enter flex flex-col h-full" data-testid="mr-detail">
        <div className="px-5 pt-3 pb-3 flex items-center justify-between">
          <button
            data-testid="mr-detail-back"
            onClick={() => { setSelectedId(null); cancelEdit(); }}
            aria-label="Volver a la lista"
            className="tap-44 w-11 h-11 rounded-full bg-white border border-[#DDE6F7] flex items-center justify-center text-brand-deep hover:text-brand-violet hover:border-brand-violet"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-[12px] font-extrabold px-2.5 py-1 rounded-md" style={{ background: estado.bg, color: estado.color }} data-testid="mr-detail-estado">
            {estado.label}
          </span>
        </div>

        <div className="flex-1 px-5 pb-3 space-y-3 overflow-y-auto">
          <div className="rounded-2xl bg-white border border-[#DDE6F7] p-4">
            <span className="px-2 py-1 rounded-md text-[10px] font-extrabold" style={{ background: dom?.bg, color: dom?.color }}>
              {dom?.name}
            </span>
            <h3 className="mt-2 text-lg font-extrabold text-brand-deep" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {conductaLabel(selected.conducta)}
            </h3>
            <p className="mt-1 text-xs text-brand-muted">
              {fmtFecha(selected.createdAt)} · {fmtHora(selected.createdAt)}
            </p>
          </div>

          {!editing && (
            <div className="rounded-2xl bg-white border border-[#DDE6F7] p-4 space-y-2 text-sm">
              <Field label="Curso" value={selected.curso || 'No identificado'} />
              <Field label="Estudiante" value={selected.estudiante || '—'} />
              <Field label="Contexto" value={VALUE_LABELS[selected.contexto] || '—'} />
              <Field label="Intensidad" value={VALUE_LABELS[selected.intensidad] || '—'} />
              {selected.frecuencia && <Field label="Frecuencia" value={VALUE_LABELS[selected.frecuencia] || '—'} />}
              <Field label="Fuente" value={VALUE_LABELS[selected.fuente] || '—'} />
              {selected.mode && (
                <Field label="Modo" value={selected.mode === 'rapido' ? 'Rápido' : 'Completo'} />
              )}
            </div>
          )}

          {editing && (
            <div className="rounded-2xl bg-white border border-[#DDE6F7] p-4 space-y-3" data-testid="mr-edit-form">
              <p className="text-sm font-bold text-brand-deep">Editar campos</p>
              <div>
                <span className="m-label">Estudiante</span>
                <input
                  data-testid="mr-edit-estudiante"
                  value={draftEdit.estudiante}
                  onChange={(e) => setDraftEdit((d) => ({ ...d, estudiante: e.target.value }))}
                  className="m-input"
                  placeholder="Nombre / iniciales / sin identificar"
                />
              </div>
              <div>
                <span className="m-label">Contexto</span>
                <div className="flex flex-wrap gap-2">
                  {CONTEXTO.map((c) => (
                    <button
                      key={c.v}
                      data-testid={`mr-edit-contexto-${c.v}`}
                      onClick={() => setDraftEdit((d) => ({ ...d, contexto: c.v }))}
                      className={`m-chip ${draftEdit.contexto === c.v ? 'is-active' : ''}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span className="m-label">Nota / texto observable</span>
                <textarea
                  data-testid="mr-edit-nota"
                  value={draftEdit.nota}
                  onChange={(e) => setDraftEdit((d) => ({ ...d, nota: e.target.value }))}
                  className="m-textarea resize-none"
                  rows={4}
                  placeholder="Lenguaje observable, sin diagnóstico."
                />
              </div>
              <p className="text-[11px] text-brand-muted">
                La edición crea una nueva versión y queda en auditoría. El registro original nunca se sobrescribe.
              </p>
            </div>
          )}

          {/* Evidencia */}
          {(selected.evidencia?.nota || selected.evidencia?.audioSeconds > 0 || (selected.evidencia?.fotos || []).length > 0) && !editing && (
            <div className="rounded-2xl bg-white border border-[#DDE6F7] p-4">
              <p className="text-sm font-bold text-brand-deep">Evidencia</p>
              {selected.evidencia?.nota && (
                <div className="mt-2 flex items-start gap-2 text-sm text-brand-deep">
                  <FileText className="w-4 h-4 mt-0.5 text-brand-muted shrink-0" />
                  <p className="leading-relaxed">{selected.evidencia.nota}</p>
                </div>
              )}
              {selected.evidencia?.audioSeconds > 0 && (
                <div className="mt-2 inline-flex items-center gap-2 text-xs text-brand-muted">
                  <Mic className="w-3.5 h-3.5" /> Audio · {selected.evidencia.audioSeconds}s
                </div>
              )}
              {(selected.evidencia?.fotos || []).length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {selected.evidencia.fotos.map((bg, i) => (
                    <div key={i} className="aspect-square rounded-lg border border-[#DDE6F7] flex items-center justify-center text-brand-violet" style={{ background: bg }}>
                      <Camera className="w-5 h-5 opacity-60" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Historial de versiones */}
          {(selected.versions || []).length > 0 && !editing && (
            <div className="rounded-2xl bg-[#EEF4FF] border border-[#DDE6F7] p-4" data-testid="mr-versions">
              <div className="flex items-center gap-2">
                <HistoryIcon className="w-4 h-4 text-brand-violet" />
                <p className="text-sm font-bold text-brand-deep">Historial de cambios ({selected.versions.length})</p>
              </div>
              <ul className="mt-2 space-y-1.5 text-[11.5px] text-brand-deep">
                {selected.versions.slice().reverse().map((v, i) => (
                  <li key={i} className="border-l-2 border-brand-violet/40 pl-2">
                    {fmtFecha(v.versionedAt)} · {fmtHora(v.versionedAt)} — {v.action === 'marcado_erroneo' ? 'Marcado como erróneo' : 'Editado'} por <strong>{v.versionedBy}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!editable && !editing && (
            <div className="rounded-2xl bg-white border border-[#DDE6F7] p-3 text-xs text-brand-muted leading-relaxed flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" />
              Este registro ya no es editable porque {estadoKey === 'marcado_erroneo'
                ? 'fue marcado como erróneo.'
                : 'el equipo de convivencia lo está revisando.'}
            </div>
          )}
        </div>

        <div className="px-5 pb-6 pt-3 space-y-2 border-t border-[#F1F4FA] bg-white">
          {editing ? (
            <>
              <button data-testid="mr-edit-save" onClick={saveEdit} className="m-btn-primary">
                <Save className="w-4 h-4" /> Guardar nueva versión
              </button>
              <button data-testid="mr-edit-cancel" onClick={cancelEdit} className="m-btn-ghost">
                <X className="w-4 h-4" /> Cancelar
              </button>
            </>
          ) : (
            <>
              {editable && (
                <button data-testid="mr-detail-edit" onClick={startEdit} className="m-btn-primary">
                  <Pencil className="w-4 h-4" /> Editar registro
                </button>
              )}
              {estadoKey !== 'marcado_erroneo' && (
                <button data-testid="mr-detail-marcar-erroneo" onClick={marcarErroneo} className="m-btn-ghost" style={{ color: '#C73838', borderColor: '#F4C9C9' }}>
                  <AlertOctagon className="w-4 h-4" /> Marcar como registro erróneo
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // === LISTA ===
  return (
    <div className="step-enter flex flex-col h-full">
      <div className="px-5 pt-3 pb-4">
        <div className="flex items-center justify-between">
          <button
            data-testid="mr-back"
            onClick={onBack}
            aria-label="Volver al inicio"
            className="tap-44 w-11 h-11 rounded-full bg-white border border-[#DDE6F7] flex items-center justify-center text-brand-deep hover:text-brand-violet hover:border-brand-violet"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-[13px] font-bold text-brand-violet">Mis Registros</span>
        </div>
        <h2 className="mt-4 text-2xl font-extrabold text-brand-deep" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Tus observaciones
        </h2>
        <p className="mt-1 text-sm text-brand-muted">
          {loading ? 'Cargando…' : (propias.length === 0 ? 'Aún no hay registros propios.' : `${propias.length} ${propias.length === 1 ? 'registro' : 'registros'} en Supabase.`)}
        </p>
      </div>

      <div className="flex-1 px-5 pb-4 space-y-2 overflow-y-auto" data-testid="mr-list">
        {propias.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-[#DDE6F7] p-7 text-center" data-testid="mr-empty">
            <span className="w-12 h-12 rounded-2xl bg-[#EEF4FF] text-brand-violet flex items-center justify-center mx-auto">
              <Sparkles className="w-6 h-6" />
            </span>
            <p className="mt-4 text-sm font-bold text-brand-deep">Aún no hay observaciones</p>
            <p className="mt-1 text-xs text-brand-muted">Cuando registres una, aparecerá aquí.</p>
          </div>
        )}

        {propias.map((o, idx) => {
          const dom = DOMAINS.find((d) => d.id === o.dominio?.id) || o.dominio;
          const estado = REGISTRO_ESTADOS[o.estado || 'registrado'];
          return (
            <button
              key={o.id || idx}
              data-testid={`mr-item-${idx}`}
              onClick={() => setSelectedId(o.id)}
              className="w-full text-left rounded-2xl bg-white border border-[#DDE6F7] p-4 hover:border-brand-violet transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-md text-[10px] font-extrabold"
                  style={{ background: dom?.bg, color: dom?.color }}>
                  {dom?.name || 'Dominio'}
                </span>
                {o.sync_estado && o.sync_estado !== 'sincronizado' && (
                  <span
                    data-testid={`mr-item-${idx}-sync`}
                    className={`px-2 py-1 rounded-md text-[10px] font-extrabold ${
                      o.sync_estado === 'fallido'
                        ? 'bg-[#FCDCDC] text-[#7C1F1F]'
                        : 'bg-[#FFF1D6] text-[#7A4A0B]'
                    }`}
                  >
                    {o.sync_estado === 'fallido' ? '✕ Error de envío' : '⟳ Pendiente de envío'}
                  </span>
                )}
                {o.sync_estado === 'sincronizado' && (
                  <span
                    data-testid={`mr-item-${idx}-sync`}
                    className="px-2 py-1 rounded-md text-[10px] font-extrabold bg-[#DFF6EC] text-[#0F6A4A]"
                  >
                    ✓ Sincronizado
                  </span>
                )}
                <span className="ml-auto px-2 py-1 rounded-md text-[10px] font-extrabold" style={{ background: estado.bg, color: estado.color }} data-testid={`mr-item-${idx}-estado`}>
                  {estado.label}
                </span>
              </div>
              <p className="mt-2 text-sm font-bold text-brand-deep">{conductaLabel(o.conducta)}</p>
              <p className="mt-1 text-xs text-brand-muted">
                {fmtFecha(o.createdAt)} · {fmtHora(o.createdAt)} · {o.curso || 'No identificado'}
              </p>
              <div className="mt-2 flex items-center justify-end text-[11px] font-bold text-brand-violet">
                Ver detalle <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-brand-muted text-[12.5px] font-semibold">{label}</span>
    <span className="text-brand-deep font-bold text-right max-w-[220px] truncate">{value}</span>
  </div>
);

export default ScreenMisRegistros;
